import { type ReactNode } from 'react';

export interface PageHeaderProps {
  readonly title: string;
  readonly subtitle?: string;
  /** Right-aligned slot — typically buttons / quick actions. */
  readonly actions?: ReactNode;
  /** Optional back-button click handler. Caller owns navigation
   * (avoids react-router-dom hard dependency in @ferro/ui). */
  readonly onBack?: () => void;
  readonly className?: string;
}

/**
 * Page-level title block: title + subtitle + right-aligned actions, plus an
 * optional back button. Mirrors HTHD admin's `PageHeader.tsx`; `backTo`
 * (route string) is replaced by `onBack` so the package stays
 * router-agnostic.
 */
export function PageHeader({
  title,
  subtitle,
  actions,
  onBack,
  className = '',
}: PageHeaderProps): JSX.Element {
  const cls = `ferro-page-header${className ? ` ${className}` : ''}`;
  return (
    <header className={cls}>
      <div className="ferro-page-header__left">
        {onBack ? (
          <button
            type="button"
            className="ferro-page-header__back"
            onClick={onBack}
            aria-label="Go back"
          >
            <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
        ) : null}
        <div className="ferro-page-header__heading">
          <h1 className="ferro-page-header__title">{title}</h1>
          {subtitle ? <p className="ferro-page-header__subtitle">{subtitle}</p> : null}
        </div>
      </div>
      {actions ? <div className="ferro-page-header__actions">{actions}</div> : null}
    </header>
  );
}
